import React, { useState, useEffect } from "react"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/shared/components/ui/card"
import { Button } from "@/shared/components/ui/button"
import { Input } from "@/shared/components/ui/input"
import { Label } from "@/shared/components/ui/label"
import { Textarea } from "@/shared/components/ui/textarea"
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/shared/components/ui/tabs"
import { useToast } from "@/shared/components/Toast"
import { Save, Building2, Phone, MapPin, Clock, Share2, Loader2 } from "lucide-react"
import type { CompanyInfoData, CompanyInfoUpdateItem } from "@/shared/types/CompanyInfo"

const CompanyInfo: React.FC = () => {
  const { showToast } = useToast()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [companyInfo, setCompanyInfo] = useState<CompanyInfoData>({})
  const [activeTab, setActiveTab] = useState('contact')
  
  // Form state
  const [formData, setFormData] = useState({
    // Contact
    company_name: '',
    company_tagline: '',
    company_description: '',
    email_primary: '',
    phone_mobile: '',
    phone_landline_1: '',
    phone_landline_2: '',
    founded_year: '',
    
    // Address
    address_line_1: '',
    address_line_2: '',
    address_city: '',
    address_country: '',
    address_postal_code: '',
    address_full: '',
    
    // Business Hours
    business_hours_weekday: '',
    business_hours_saturday: '',
    business_hours_sunday: '',
    
    // Social Media
    social_facebook: '',
    social_instagram: '',
    social_youtube: '',
    social_twitter: '',
    social_linkedin: ''
  })

  useEffect(() => {
    fetchCompanyInfo()
  }, [])

  const fetchCompanyInfo = async () => {
    try {
      setLoading(true)

      
      const response = await fetch('/api/company-info.php', {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json'
        }
      })
      

      
      if (!response.ok) {
        const errorText = await response.text()
        console.error('Response error text:', errorText)
        throw new Error(`HTTP ${response.status}: ${errorText}`)
      }
      
      const data = await response.json()
      console.log('Response data success:', data.success)
      console.log('Response data count:', data.count)
      
      if (data.success && data.data) {
        setCompanyInfo(data.data)
        
        // Populate form with current values
        const newFormData: any = {}
        Object.keys(formData).forEach(key => {
          newFormData[key] = data.data[key]?.value || ''
        })
        setFormData(newFormData)
        console.log('Company info loaded successfully')
      } else {
        console.error('API returned success=false:', data)
        showToast({
          title: "Error",
          message: data.message || "Failed to fetch company information",
          type: "error"
        })
      }
    } catch (error) {
      console.error('Fetch error:', error)
      showToast({
        title: "Error",
        message: error instanceof Error ? error.message : "Failed to fetch company information",
        type: "error"
      })
    } finally {
      setLoading(false)
    }
  }

  const handleInputChange = (key: string, value: string) => {
    setFormData(prev => ({
      ...prev,
      [key]: value
    }))
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      
      // Prepare updates array
      const updates: CompanyInfoUpdateItem[] = Object.entries(formData).map(([key, value]) => {
        const category = getCategory(key)
        const desc = companyInfo[key]?.description
        return {
          key,
          value,
          category,
          description: desc || undefined
        }
      })
      
      const response = await fetch('/api/company-info.php', {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ updates })
      })
      
      const data = await response.json()
      
      if (data.success) {
        showToast({
          title: "Success",
          message: "Company information updated successfully",
          type: "success"
        })
        fetchCompanyInfo() // Refresh data
      } else {
        showToast({
          title: "Error",
          message: data.message || "Failed to update company information",
          type: "error"
        })
      }
    } catch {
      showToast({
        title: "Error",
        message: "Failed to update company information",
        type: "error"
      })
    } finally {
      setSaving(false)
    }
  }

  const getCategory = (key: string): 'contact' | 'social' | 'business_hours' | 'address' => {
    if (key.startsWith('social_')) return 'social'
    if (key.startsWith('address_')) return 'address'
    if (key.startsWith('business_hours_')) return 'business_hours'
    return 'contact'
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-orange-500" />
      </div>
    )
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-2">
            <Building2 className="h-8 w-8 text-orange-500" />
            Company Information
          </h1>
          <p className="text-gray-600 mt-2">
            Manage your company contact details, address, and social media links
          </p>
        </div>
        <Button 
          onClick={handleSave}
          disabled={saving}
          className="bg-orange-500 hover:bg-orange-600 text-white"
        >
          {saving ? (
            <>
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
              Saving...
            </>
          ) : (
            <>
              <Save className="w-4 h-4 mr-2" />
              Save Changes
            </>
          )}
        </Button>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 gap-2">
          <TabsTrigger value="contact">
            <Phone className="w-4 h-4 mr-2" />
            Contact
          </TabsTrigger>
          <TabsTrigger value="address">
            <MapPin className="w-4 h-4 mr-2" />
            Address
          </TabsTrigger>
          <TabsTrigger value="hours">
            <Clock className="w-4 h-4 mr-2" />
            Business Hours
          </TabsTrigger>
          <TabsTrigger value="social">
            <Share2 className="w-4 h-4 mr-2" />
            Social Media
          </TabsTrigger>
        </TabsList>

        {/* Contact Information Tab */}
        <TabsContent value="contact">
          <Card>
            <CardHeader>
              <CardTitle>Contact Information</CardTitle>
              <CardDescription>
                Update your company contact details displayed on the website
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="company_name">Company Name</Label>
                  <Input
                    id="company_name"
                    value={formData.company_name}
                    onChange={(e) => handleInputChange('company_name', e.target.value)}
                    placeholder="Travel Warehouse Inc."
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="founded_year">Founded Year</Label>
                  <Input
                    id="founded_year"
                    value={formData.founded_year}
                    onChange={(e) => handleInputChange('founded_year', e.target.value)}
                    placeholder="1999"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_tagline">Company Tagline</Label>
                <Input
                  id="company_tagline"
                  value={formData.company_tagline}
                  onChange={(e) => handleInputChange('company_tagline', e.target.value)}
                  placeholder="Your trusted partner for exceptional travel experiences"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="company_description">Company Description</Label>
                <Textarea
                  id="company_description"
                  value={formData.company_description}
                  onChange={(e) => handleInputChange('company_description', e.target.value)}
                  placeholder="Creating unforgettable memories around the world."
                  rows={3}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="email_primary">Primary Email</Label>
                  <Input
                    id="email_primary"
                    type="email"
                    value={formData.email_primary}
                    onChange={(e) => handleInputChange('email_primary', e.target.value)}
                    placeholder="info@twi.com.ph"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone_mobile">Mobile Phone</Label>
                  <Input
                    id="phone_mobile"
                    value={formData.phone_mobile}
                    onChange={(e) => handleInputChange('phone_mobile', e.target.value)}
                    placeholder="(+63) 985 590 9000"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="phone_landline_1">Landline 1</Label>
                  <Input
                    id="phone_landline_1"
                    value={formData.phone_landline_1}
                    onChange={(e) => handleInputChange('phone_landline_1', e.target.value)}
                    placeholder="(02) 8687-2490"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="phone_landline_2">Landline 2</Label>
                  <Input
                    id="phone_landline_2"
                    value={formData.phone_landline_2}
                    onChange={(e) => handleInputChange('phone_landline_2', e.target.value)}
                    placeholder="(02) 8687-2491"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Address Tab */}
        <TabsContent value="address">
          <Card>
            <CardHeader>
              <CardTitle>Address Information</CardTitle>
              <CardDescription>
                Update your company address displayed on the website
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="address_line_1">Address Line 1</Label>
                <Input
                  id="address_line_1"
                  value={formData.address_line_1}
                  onChange={(e) => handleInputChange('address_line_1', e.target.value)}
                  placeholder="1007 OMM Citra Bldg, San Miguel Ave."
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="address_line_2">Address Line 2</Label>
                <Input
                  id="address_line_2"
                  value={formData.address_line_2}
                  onChange={(e) => handleInputChange('address_line_2', e.target.value)}
                  placeholder="Ortigas Center, Pasig City"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="address_city">City</Label>
                  <Input
                    id="address_city"
                    value={formData.address_city}
                    onChange={(e) => handleInputChange('address_city', e.target.value)}
                    placeholder="Pasig City"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="address_country">Country</Label>
                  <Input
                    id="address_country"
                    value={formData.address_country}
                    onChange={(e) => handleInputChange('address_country', e.target.value)}
                    placeholder="Philippines"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="address_postal_code">Postal Code</Label>
                  <Input
                    id="address_postal_code"
                    value={formData.address_postal_code}
                    onChange={(e) => handleInputChange('address_postal_code', e.target.value)}
                    placeholder="1600"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="address_full">Full Address (Single Line)</Label>
                <Textarea
                  id="address_full"
                  value={formData.address_full}
                  onChange={(e) => handleInputChange('address_full', e.target.value)}
                  placeholder="Unit 1007 OMM Citra Bldg. San Miguel Ave. Ortigas Center, Pasig City, Philippines 1600"
                  rows={2}
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Business Hours Tab */}
        <TabsContent value="hours">
          <Card>
            <CardHeader>
              <CardTitle>Business Hours</CardTitle>
              <CardDescription>
                Set your operating hours for different days
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="business_hours_weekday">Weekday Hours</Label>
                <Input
                  id="business_hours_weekday"
                  value={formData.business_hours_weekday}
                  onChange={(e) => handleInputChange('business_hours_weekday', e.target.value)}
                  placeholder="Monday - Friday: 8:30 AM - 5:30 PM"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="business_hours_saturday">Saturday Hours</Label>
                <Input
                  id="business_hours_saturday"
                  value={formData.business_hours_saturday}
                  onChange={(e) => handleInputChange('business_hours_saturday', e.target.value)}
                  placeholder="Closed or 9:00 AM - 2:00 PM"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="business_hours_sunday">Sunday Hours</Label>
                <Input
                  id="business_hours_sunday"
                  value={formData.business_hours_sunday}
                  onChange={(e) => handleInputChange('business_hours_sunday', e.target.value)}
                  placeholder="Closed"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Social Media Tab */}
        <TabsContent value="social">
          <Card>
            <CardHeader>
              <CardTitle>Social Media Links</CardTitle>
              <CardDescription>
                Update your social media profile URLs
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="social_facebook">Facebook Page URL</Label>
                <Input
                  id="social_facebook"
                  value={formData.social_facebook}
                  onChange={(e) => handleInputChange('social_facebook', e.target.value)}
                  placeholder="https://www.facebook.com/travelwarehouseinc"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="social_instagram">Instagram Profile URL</Label>
                <Input
                  id="social_instagram"
                  value={formData.social_instagram}
                  onChange={(e) => handleInputChange('social_instagram', e.target.value)}
                  placeholder="https://www.instagram.com/twirista/"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="social_youtube">YouTube Channel URL</Label>
                <Input
                  id="social_youtube"
                  value={formData.social_youtube}
                  onChange={(e) => handleInputChange('social_youtube', e.target.value)}
                  placeholder="https://www.youtube.com/@TWIrista"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="social_twitter">Twitter Profile URL (Optional)</Label>
                <Input
                  id="social_twitter"
                  value={formData.social_twitter}
                  onChange={(e) => handleInputChange('social_twitter', e.target.value)}
                  placeholder="https://twitter.com/yourhandle"
                />
              </div>

              <div className="space-y-2">
                <Label htmlFor="social_linkedin">LinkedIn Profile URL (Optional)</Label>
                <Input
                  id="social_linkedin"
                  value={formData.social_linkedin}
                  onChange={(e) => handleInputChange('social_linkedin', e.target.value)}
                  placeholder="https://www.linkedin.com/company/yourcompany"
                />
              </div>
            </CardContent>
          </Card>
        </TabsContent>


      </Tabs>
    </div>
  )
}

export default CompanyInfo
